import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

interface BarcodeScannerProps {
  onDetected: (code: string) => void;
  onClose: () => void;
}

export function BarcodeScanner({ onDetected, onClose }: BarcodeScannerProps) {
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(true);
  const onDetectedRef = useRef(onDetected);
  onDetectedRef.current = onDetected;

  const stableOnDetected = useCallback((code: string) => {
    onDetectedRef.current(code);
  }, []);

  useEffect(() => {
    const elementId = 'barcode-scanner-region';
    const scanner = new Html5Qrcode(elementId);
    let stopped = false;

    scanner
      .start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 280, height: 160 },
          aspectRatio: 1.5,
        },
        (decodedText) => {
          if (stopped) return;
          stopped = true;
          scanner
            .stop()
            .then(() => stableOnDetected(decodedText))
            .catch(() => stableOnDetected(decodedText));
        },
        () => {},
      )
      .then(() => setStarting(false))
      .catch((err) => {
        setStarting(false);
        if (String(err).includes('NotAllowedError') || String(err).includes('Permission')) {
          setError('Camera access denied. Please allow camera permissions and try again.');
        } else if (String(err).includes('NotFoundError') || String(err).includes('no camera')) {
          setError('No camera found on this device.');
        } else {
          setError(`Could not start camera: ${String(err)}`);
        }
      });

    return () => {
      if (!stopped) {
        stopped = true;
        scanner.stop().catch(() => {});
      }
    };
  }, [stableOnDetected]);

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <span style={styles.title}>Scan Barcode</span>
          <button onClick={onClose} style={styles.closeButton} aria-label="Close scanner">
            &#x2715;
          </button>
        </div>

        <div style={styles.scannerContainer}>
          <div id="barcode-scanner-region" style={styles.scannerRegion} />
          {starting && !error && (
            <div style={styles.startingOverlay}>
              <div style={styles.spinner} />
              <span style={styles.startingText}>Starting camera...</span>
            </div>
          )}
          {error && (
            <div style={styles.errorOverlay}>
              <span style={styles.errorText}>{error}</span>
              <button onClick={onClose} style={styles.retryButton}>Close</button>
            </div>
          )}
        </div>

        <p style={styles.hint}>
          Point the camera at a barcode on the product tag
        </p>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '16px',
  },
  modal: {
    background: '#1a1a1a',
    borderRadius: '16px',
    border: '1px solid #2d3748',
    width: '100%',
    maxWidth: '480px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #2d3748',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#f7fafc',
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: '#a0aec0',
    fontSize: '20px',
    cursor: 'pointer',
    padding: '4px 8px',
    borderRadius: '6px',
    lineHeight: 1,
  },
  scannerContainer: {
    position: 'relative',
    width: '100%',
    aspectRatio: '1.5',
    background: '#000',
  },
  scannerRegion: {
    width: '100%',
    height: '100%',
  },
  startingOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    background: 'rgba(0,0,0,0.7)',
  },
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #2d3748',
    borderTopColor: '#3182ce',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
  startingText: {
    color: '#a0aec0',
    fontSize: '14px',
  },
  errorOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    background: 'rgba(0,0,0,0.8)',
    padding: '24px',
  },
  errorText: {
    color: '#fc8181',
    fontSize: '15px',
    textAlign: 'center',
    lineHeight: 1.5,
  },
  retryButton: {
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: 600,
    background: '#2d3748',
    color: '#f7fafc',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  hint: {
    padding: '16px 20px',
    margin: 0,
    color: '#718096',
    fontSize: '14px',
    textAlign: 'center',
  },
};
